import {FC, useEffect, useRef, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {useLayout} from '../../../_metronic/layout/core'
import {showErrorAlert} from '../../helpers/alert'
import {handleApiError} from '../../utils/handleApiError'
import {getExpertDetail} from '../../services/expertDirectoryService'
import {createEngagement, CreateEngagementPayload} from '../../services/engagementService'
import {Expert, ownerTypeLabel} from './expertDirectory'

// WP-X6 — request an engagement from an expert's detail (scope / proposed amount / terms → PROPOSED).
// Creates against the deployed #43 API. NOTE: the engagement create needs the expert's underlying
// member/partner id (expert_member / expert_partner). X4's public serializer must expose those
// (expertMemberId / expertPartnerId) — FLAGGED. Until it does, the form explains it can't submit yet.
const EngagementRequestPage: FC = () => {
  const {id} = useParams<{id: string}>()
  const history = useHistory()
  const {config, setLayout} = useLayout()
  const restoreRef = useRef(config)

  const [expert, setExpert] = useState<Expert | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({
    scope: '',
    agreedAmount: '',
    currency: 'usd',
    terms: '',
    cancellationTerms: '',
  })

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: {...config.header, display: true},
      aside: {...config.aside, display: true},
      toolbar: {...config.toolbar, display: false},
      footer: {...config.footer, display: true},
    })
    return () => setLayout(restoreRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let active = true
    getExpertDetail(id)
      .then((data) => active && setExpert(data))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [id])

  // The expert reference the create endpoint needs (X4 must expose these on the public serializer).
  const expertMemberId = (expert as any)?.expertMemberId
  const expertPartnerId = (expert as any)?.expertPartnerId
  const hasRef = expert?.ownerType === 'member' ? Boolean(expertMemberId) : Boolean(expertPartnerId)

  const handleSubmit = async () => {
    if (!form.scope.trim()) {
      showErrorAlert({title: 'Scope required', message: 'Please describe the scope of work.'})
      return
    }
    const payload: CreateEngagementPayload = {
      scope: form.scope.trim(),
      agreedAmount: form.agreedAmount === '' ? null : Number(form.agreedAmount),
      currency: form.currency,
      terms: form.terms,
      cancellationTerms: form.cancellationTerms,
    }
    if (expert?.ownerType === 'member') payload.expertMember = Number(expertMemberId)
    else payload.expertPartner = Number(expertPartnerId)

    try {
      setBusy(true)
      const created = await createEngagement(payload)
      showErrorAlert({title: 'Sent', message: 'Your engagement request has been proposed.'})
      history.push(`/engagements/${created.id}`)
    } catch (err) {
      handleApiError(err, {
        onServerError: () =>
          showErrorAlert({title: 'Error', message: 'Could not send the request right now.'}),
        onBadRequest: (data: any) =>
          showErrorAlert({title: 'Error', message: 'Please review the request details.'}),
      })
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <div className='text-center py-10'>Loading…</div>
  if (error || !expert)
    return (
      <div className='text-center py-10 text-muted'>
        This expert isn’t available yet (the expert directory is blocked on the X4 deploy).
      </div>
    )

  return (
    <div className='engagement-request-page'>
      <button
        type='button'
        className='nb-link-underline-500 mb-6'
        onClick={() => history.push(`/book-expert/${id}`)}
      >
        ← Back
      </button>

      <div className='card p-8' style={{maxWidth: 720}}>
        <h1 className='mb-1'>Request an engagement</h1>
        <div className='text-muted mb-6'>
          {expert.displayName}{' '}
          {ownerTypeLabel(expert.ownerType) && `· ${ownerTypeLabel(expert.ownerType)}`}
        </div>

        {!hasRef && (
          <div className='alert alert-warning' role='alert'>
            Requesting this expert isn’t available yet — the expert directory needs to expose the
            expert reference (pending an X4 update). Your details below aren’t lost; try again once
            it’s live.
          </div>
        )}

        <div className='mb-4'>
          <label className='form-label'>Scope of work *</label>
          <textarea
            className='form-control'
            rows={4}
            placeholder='What do you need help with?'
            value={form.scope}
            onChange={(e) => setForm({...form, scope: e.target.value})}
          />
        </div>
        <div className='d-flex gap-4'>
          <div className='mb-4 flex-grow-1'>
            <label className='form-label'>Proposed amount</label>
            <input
              type='number'
              className='form-control'
              value={form.agreedAmount}
              onChange={(e) => setForm({...form, agreedAmount: e.target.value})}
            />
          </div>
          <div className='mb-4' style={{width: 120}}>
            <label className='form-label'>Currency</label>
            <input
              className='form-control'
              value={form.currency}
              onChange={(e) => setForm({...form, currency: e.target.value})}
            />
          </div>
        </div>
        <div className='mb-4'>
          <label className='form-label'>Terms</label>
          <textarea
            className='form-control'
            rows={3}
            value={form.terms}
            onChange={(e) => setForm({...form, terms: e.target.value})}
          />
        </div>
        <div className='mb-6'>
          <label className='form-label'>Cancellation terms</label>
          <textarea
            className='form-control'
            rows={2}
            value={form.cancellationTerms}
            onChange={(e) => setForm({...form, cancellationTerms: e.target.value})}
          />
        </div>

        <button className='btn nb-btn-primary' disabled={busy || !hasRef} onClick={handleSubmit}>
          Send request
        </button>
      </div>
    </div>
  )
}

export default EngagementRequestPage
